/* Example Usage

    ---------------------------------------------

    First, enhance the data with the props required for EnhancedTable:

    let enhancedData = [];
    enhancedData = this.state.organizations.map((organization) => {
        let org = JSON.parse(JSON.stringify(organization))

        // Add any calculated parameters
        org.organizationCode = (this.categoryCodeMap[org.categoryId] || "") + org.organizationCode;
        org.categoryName = this.categoryMap[org.categoryId] || "-";
        return org;
    });

    const headCells = [
        { id: 'actions', centered: false, sortable: false, label: 'Actions' },
        { id: 'organizationName', centered: false, sortable: true, label: 'Name' },
        { id: 'organizationCode', centered: true, sortable: true, label: 'Code' },
        { id: 'categoryName', centered: true, sortable: true, label: 'Category' },
    ];

    const bodyCells = [
        { name: 'organizationName', centered: false },
        { name: 'organizationCode', centered: true },
        { name: 'categoryName', centered: true },
    ];

    ---------------------------------------------

    Usage:
    
    <EnhancedTable
        data={enhancedData}
        headCells={headCells}
        bodyCells={bodyCells}
        defaultSortKey={"organizationName"}
        cellKey={"ID"}
        editDelete={true}
        edit={this.onEdit}
        delete={this.openDeleteDialog}
    />

*   Parameters/Props:
        data:  array [required]
        headCells:  array [required]
        bodyCells:  array [required]
        defaultSortKey:  string [required]
        cellKey:  string [required]
        editDelete:  boolean [required] (decides whether edit and delete buttons are rendered)
        edit:  function [required if editDelete is true]
        delete:  function [required if editDelete is true]
*/

import React from 'react';
import PropTypes from 'prop-types';
import makeStyles from '@mui/styles/makeStyles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BlockIcon from '@mui/icons-material/Block';
import EventSeatIcon from '@mui/icons-material/EventSeat';
import WavesIcon from '@mui/icons-material/Waves';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import PersonIcon from '@mui/icons-material/Person';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
// import RemoveIcon from '@mui/icons-material/Remove';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import AssignmentIcon from '@mui/icons-material/Assignment';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import TurnedInIcon from '@mui/icons-material/TurnedIn';

function getIcon(iconName) {
    switch (iconName) {
        case 'edit':
            return (<EditIcon />); //pencil
        case 'delete':
            return (<DeleteIcon />); //trash can
        case 'view':
            return (<VisibilityIcon />); //eye
        case 'cancel':
            return (<BlockIcon />); // circle with diagnoal line through it
        case 'register':
            return (<EventSeatIcon />); // an armchair
        case 'makeDefaultContact':
            return (<ContactPhoneIcon />);
        case 'waiver':
            return <WavesIcon /> // 3 wavy lines
        case 'accept':
            return <CheckCircleIcon /> //circle with check
        case 'lock':
            return <LockOpenIcon /> //partailly open lock
        case 'profile':
            return <PersonIcon /> //person head
        case 'add':
            return <AddCircleOutlineIcon /> // + in circle
        case 'remove':
            return <RemoveCircleIcon /> // - in circle
        case 'up':
            return <KeyboardArrowUpIcon /> //up arrow
        case 'down':
            return <KeyboardArrowDownIcon /> //down arrow
        case 'upDown':
            return <ImportExportIcon /> //up down arrow
        case 'play':
            return <PlayCircleOutlineIcon /> // Play icon in cirle
        case 'quiz':
            return <AssignmentIcon /> // a clipboard icon, used for take quiz
        case 'review':
            return <FindInPageIcon /> // a page with magnifying glass
        case 'requestcertificate':
            return <TurnedInIcon />
        default:
            return null;
    }
}

function descendingComparator(a, b, orderBy) {
    // console.log('New Compare');
    // console.log('b is ', b[orderBy]);
    // console.log('a is ', a[orderBy]);

    // console.log('instanceof', Date(b[orderBy]) instanceof Date );
    // console.log('date', typeof(Date(b[orderBy])) );
    // console.log('date parse', Date.parse(b[orderBy]) );

    // if( typeof(b[orderBy]) ){}

    if(Date.parse(b[orderBy]) || Date.parse(a[orderBy])   ){
        if (b[orderBy] === null || Date.parse(b[orderBy]) < Date.parse(a[orderBy]) ){
            return -1;
        }
        if(a[orderBy] === null || Date.parse(b[orderBy]) > Date.parse(a[orderBy])){
            return 1;
        }
    }


    if ( b[orderBy] === null || 
        ((b[orderBy] && b[orderBy].toLowerCase()) < 
        (a[orderBy] && a[orderBy].toLowerCase())) ) {

        return -1;
    }
    if ( a[orderBy] === null || 
        ((b[orderBy] &&b[orderBy].toLowerCase()) > 
        (a[orderBy] && a[orderBy].toLowerCase())) ) {
        return 1;
    }
    return 0;
}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

function TableActions(props) {
    // let actionsDisabled = props.actionsDisabled || null;

    return (
        <TableCell key="actions" style={props.style}>
            <Grid container  alignItems="center"   >
                {props.actionButtons.map((button) => (
                    <span key={button.icon} className='gridItemSpacing'>
                        {
                            // remove the button if the view attribute is defined and the action disabled prop for that is false
                            (!button.view || (button.view && props.actionsDisabled[button.view])) &&
                            <Button
                                variant="contained"
                                size="small"
                                data-tag={props.cellKey}
                                className={button.delete ? props.classes.deleteButton : ""}
                                onClick={() => button.action(props.item[button.attribute] || props.cellKey)}
                                // disabled={actionsDisabled && actionsDisabled[button.icon] || false}
                                disabled={props.actionsDisabled && props.actionsDisabled[button.icon]}
                                startIcon={getIcon(button.icon)}
                            >
                                {button.label ? button.label : button.icon}
                            </Button>}
                    </span>
                ))}
            </Grid>
        </TableCell>
    );
}

function EnhancedTableHead(props) {
    const { classes, order, orderBy, onRequestSort } = props;
    const createSortHandler = (property) => (event) => {
        onRequestSort(event, property);
    };

    return (
        <TableHead>
            <TableRow>
                {props.headCells.map((headCell) => (
                    <TableCell
                        key={headCell.id}
                        align={headCell.centered ? "center" : "inherit"}
                        sortDirection={orderBy === headCell.id ? order : false}
                    >
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            hideSortIcon={!headCell.sortable}
                            direction={orderBy === headCell.id ? order : 'asc'}
                            onClick={headCell.sortable ? createSortHandler(headCell.id) : undefined}
                        >
                            {headCell.label}
                            {orderBy === headCell.id ? (
                                <span className={classes.visuallyHidden}>
                                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                </span>
                            ) : null}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

EnhancedTableHead.propTypes = {
    classes: PropTypes.object.isRequired,
    onRequestSort: PropTypes.func.isRequired,
    order: PropTypes.oneOf(['asc', 'desc']).isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
};

const useStyles = makeStyles((theme) => ({
    root: {
        '& .MuiTextField-root': {
            minWidth: '25ch',
        },
        '& .MuiTableCell-head': {
            fontWeight: 'bold'
        }
    },
    visuallyHidden: {
        border: 0,
        clip: 'rect(0 0 0 0)',
        height: 1,
        margin: -1,
        overflow: 'hidden',
        padding: 0,
        position: 'absolute',
        top: 20,
        width: 1,
    },
    deleteButton: {
        color: '#d90000'
    }
}));

export default function SortedTable(props) {
    const classes = useStyles();
    const items = props.data;
    const headCells = props.headCells;

    const [order, setOrder] = React.useState(props.defaultOrder || 'asc');
    const [orderBy, setOrderBy] = React.useState(props.defaultSortKey);
    //const [page, setPage] = React.useState(0);
    //const [rowsPerPage, setRowsPerPage] = React.useState(10);

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        console.log('property', property);
        setOrderBy(property);
    };

    const highlightCell = props.highlight || null
    // const handleChangePage = (event, newPage) => {
    //     setPage(newPage);
    // };

    // const handleChangeRowsPerPage = (event) => {
    //     setRowsPerPage(parseInt(event.target.value, 10));
    //     setPage(0);
    // };

    //const emptyRows = rowsPerPage - Math.min(rowsPerPage, items.length - page * rowsPerPage);

    return (
        <div className={classes.root}>
            <Paper className={classes.paper}>
            {(props.data.length > 0) &&
                <TableContainer>
                    <Table
                        className={classes.table}
                        aria-label="enhanced table"
                    >
                        <EnhancedTableHead
                            classes={classes}
                            order={order}
                            orderBy={orderBy}
                            onRequestSort={handleRequestSort}
                            rowCount={items.length}
                            headCells={headCells}
                        />
                        <TableBody>
                            {stableSort(items, getComparator(order, orderBy))
                                // .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((row, index) => {

                                    
                                    // let cells = props.bodyCells.forEach((cell) => {
                                    //     console.log('data here at cell', cell);
                                    //   //  let cellName = cell.name;
                                    //     let cellName = cell.id;
                                    //     console.log('row', row);
                                    //     console.log('data here at row', row[cellName]);
                                    //     return (<TableCell key={cell.name} align={cell.centered ? 'center' : 'inherit'}>{row[cellName]}</TableCell>)
                                    // });
                                    
                                    let cellKey = props.cellKey;
                                    let backGround = {}
                                    if (highlightCell && row[highlightCell] ) backGround = { backgroundColor: 'lightyellow' }
                                    
                                    let cells = props.bodyCells.map((cell, index) => {
                                        let cellName = cell.id || cell.name;

                                        return <TableCell key={cellName} align={cell.centered ? 'center' : 'inherit'} style={backGround}>
                                            {

                                                cell.hyperlink && row['enableLink'] ?
                                                // the react linter did not like anchor tags without an href, so I used there suggestion and did a button
                                                <button
                                                    className='link-button'
                                                    onClick={() => cell.hyperlink(row[cellKey])}
                                                >
                                                    {row[cellName]}
                                                </button> :
                                                row[cellName]
                                            }
                                        </TableCell>
                                    });

                                    return (
                                        <TableRow key={ index } data-tag={row[cellKey]}
                                        >
                                            
                                            {
        
                                            props.editDelete &&
                                                <TableCell>
                                                    <IconButton
                                                        aria-label="edit"
                                                        data-tag={row[cellKey]}
                                                        className={[].join(' ')}
                                                        onClick={() => props.edit(row[cellKey])}
                                                        size="large">
                                                        <EditIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        aria-label="delete"
                                                        data-tag={row[cellKey]}
                                                        className={[classes.deleteButton].join(' ')}
                                                        onClick={() => props.delete(row[cellKey])}
                                                        size="large">
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </TableCell>
                                            }
                                           {  
                                           props.actions &&
                                             <TableActions
                                                 classes={classes}
                                                 actionButtons={props.actionButtons}
                                                 item={row}
                                                 cellKey={row[cellKey]}
                                                 actionsDisabled={row['actionsDisabled']}
                                                 style={backGround}
                                             />
                                             
                                            }
                                            {cells}
                                        </TableRow>
                                    );

                                    

                                })}
                        </TableBody>
                    </Table>
                </TableContainer>
                            }

            </Paper>
        </div>
    );
}
